import * as React from "react";

import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsoluteFromMask,
    isModelPath,
    PathTokens,
} from "../../../Common/ModelPath/ModelPath";
import { BindingPath } from "../Engine/Core/CoreTypes";
import { IterationContext } from "../Engine/Core/Calculator2/ModelPath/IterationContext";

import { ControlDependency, UnresolvedControlDependency } from "./withRuntimeDependencies";
import { createHocDisplayName } from "./TypingUtils";
import { useInstancesStack } from "./Internals/ControlContexts/PathContexts/PushInstance";
import { useFormSettings } from "./FormObjectProvider";
import { InjectDataProps } from "./InjectDataProps";

export interface PathResolver {
    (path: BindingPath): BindingPath;
    (path: AbsoluteModelPath): AbsoluteResolvedModelPath;
    (path: BindingPath | AbsoluteModelPath): BindingPath | AbsoluteResolvedModelPath;
}

export interface TargetProps {
    bindingPath?: BindingPath | AbsoluteResolvedModelPath;
    instance?: string;
    dependencies?: ControlDependency[];
    parentPath?: BindingPath;
    resolvePath?: PathResolver;
}

export function withResolvePaths<P>(Component: React.ComponentType<P>): React.ComponentType<
    Omit<P, "bindingPath" | "resolvePath" | "dependencies"> & {
        bindingPath?: AbsoluteModelPath;
        dependencies?: UnresolvedControlDependency[];
    }
>;
export function withResolvePaths<P extends TargetProps>(Component: React.ComponentType<P>): React.ComponentType<P> {
    function ResolvePaths(props: P): JSX.Element {
        const { passBindingPathsIntoDomElements = true } = useFormSettings();
        const instances = useInstancesStack();

        const resolveLegacyPath = React.useCallback(
            (path: BindingPath): BindingPath => {
                let resultPath = createAbsoluteFromMask(path, PathTokens.current);
                for (const instanceContext of instances) {
                    const i = new IterationContext(instanceContext.bindingPath, instanceContext.instance);
                    resultPath = i.applyTo(resultPath);
                }
                return resultPath.toLegacyPath();
            },
            [instances]
        );

        const resolveModelPath = React.useCallback(
            (path: AbsoluteModelPath): AbsoluteModelPath => {
                let resultPath = path;
                for (const c of instances) {
                    const i = new IterationContext(c.bindingPath, c.instance);
                    resultPath = i.applyTo(resultPath);
                }
                return resultPath;
            },
            [instances]
        );

        // @ts-expect-error Тайпскрипт тут пока не может
        const resolvePath: PathResolver = React.useCallback(
            path => (isModelPath(path) ? resolveModelPath(path) : resolveLegacyPath(path)),
            [instances]
        );

        const resolvedBindingPath = React.useMemo(
            () => props.bindingPath && resolvePath(props.bindingPath),
            [props.bindingPath, instances]
        );
        const resolvedDependencies = React.useMemo(
            () =>
                props.dependencies != undefined && props.dependencies.length > 0
                    ? props.dependencies.map(dependent => ({
                          ...dependent,
                          path: resolvePath(dependent.path),
                      }))
                    : undefined,
            [props.dependencies, instances]
        );

        const nextProps: P = { ...props };
        nextProps.resolvePath = resolvePath;
        if (resolvedBindingPath != undefined) {
            nextProps.bindingPath = resolvedBindingPath;
        }
        if (resolvedDependencies != undefined) {
            nextProps.dependencies = resolvedDependencies;
        }

        if (passBindingPathsIntoDomElements) {
            return (
                <InjectDataProps
                    dataAttributes={{
                        ["data-candy-path"]: props.bindingPath?.toString(),
                        ["data-candy-resolved-path"]: nextProps.bindingPath?.toString(),
                    }}>
                    <Component {...nextProps} />
                </InjectDataProps>
            );
        }

        return <Component {...nextProps} />;
    }

    ResolvePaths.displayName = createHocDisplayName(Component, "ResolvePaths");
    return ResolvePaths;
}
